// @flow
import React from 'react';
import {
  makeStyles,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
} from '@material-ui/core';

import {EditOptionImage} from '../../../products/components/ProductVariants/ConfirmVariantEditModal/ConfirmVariantEditModal';

import type {
  ProductVariantOptionDisplayType,
  ProductVariantOptionImageShape,
} from '../../../../entities';
import type {OptionValue} from './EditVariantForm';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import {SortableKnob} from 'react-easy-sort';
import {DragIndicator, Delete, HelpOutline} from '@material-ui/icons';
import {colors} from '../../../../assets/styles/colors';
import {useListProducts} from '../../../products/utils/hooks';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';
import {useToggle} from '../../../../utils/react';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';
import {getImageUrl} from '../../../../utils/image_provider';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    gap: '16px',
    flexGrow: 1,
    alignItems: 'center',
    '& img': {
      width: '40px',
      height: '40px',
      maxWidth: '40px',
      maxHeight: '40px',
    },
    padding: '8px',
  },
  variantItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: colors.grey200,
    borderRadius: '12px',
    position: 'relative',
    width: '100%',
  },
  colorWrap: {
    height: 40,
    width: 40,
  },
  noImage: {
    height: 40,
    width: 40,
  },
  knob: {
    position: 'relative',
    flexShrink: 0,
    height: 64,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'grab',
    paddingInline: '8px 4px',
  },
  order: {
    fontWeight: 500,
    fontSize: 14,
  },
  deleteButton: {
    color: colors.danger,
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1,
    top: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  list: {
    maxHeight: 300,
    overflow: 'auto',
  },
}));

type Props = {
  value: OptionValue,
  displayType: ProductVariantOptionDisplayType,
  imageShape: ProductVariantOptionImageShape,
  onUpdateValue: (o: OptionValue) => void,
  onDeleteValue: () => void,
  order: number,
};

const EditVariantValue = ({
  displayType,
  value,
  imageShape,
  onUpdateValue,
  onDeleteValue,
  order,
}: Props) => {
  const classes = useStyles();

  const {fetchProducts, loading, products} = useListProducts();
  const [confirmModalOpen, toggleConfirmModalOpen] = useToggle();

  const handleDelete = () => {
    if (!value.id) return;
    fetchProducts({
      variant_option_values: [value.id],
    }).then(() => {
      toggleConfirmModalOpen();
    });
  };

  return (
    <div className={classes.variantItem}>
      {loading && (
        <span className={classes.loader}>
          <InlineLoader loading size={20} />
        </span>
      )}
      <SortableKnob>
        <span className={classes.knob}>
          <DragIndicator className={classes.dragIcon} />
        </span>
      </SortableKnob>
      <span className={classes.order}>{order + 1}</span>
      <div className={classes.root}>
        <EditOptionImage
          imageShape={imageShape}
          displayType={displayType}
          setNewValue={onUpdateValue}
          optionValue={value}
        />
        <CustomTextField
          fullWidth
          value={value.id ? value.label : value.name}
          onChange={e => {
            const key: string = value.id ? 'label' : 'name';
            onUpdateValue({
              ...value,
              [key]: e.target.value,
            });
          }}
        />

        <IconButton className={classes.deleteButton} onClick={handleDelete}>
          <Delete />
        </IconButton>
      </div>

      {confirmModalOpen && (
        <ConfirmModal
          open={confirmModalOpen}
          title="Esta acción no se puede deshacer"
          message={
            <div style={{marginTop: '16px'}}>
              {products.length === 0 ? (
                <>
                  <Typography
                    variant="body2"
                    align="center"
                    style={{fontWeight: 'bold'}}
                  >
                    ¿Estás seguro de eliminar esta opción?
                  </Typography>

                  <Typography variant="body2" style={{marginTop: '16px'}}>
                    Esta opción no está asociada a ningún producto
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2">
                    Al eliminar esta opción, se <strong>desactivará</strong> de
                    todos los productos en los que se encuentre.
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{marginTop: '16px', fontWeight: 'bold'}}
                  >
                    Productos afectados:
                  </Typography>
                </>
              )}
              <List dense className={classes.list}>
                {products.map(product => {
                  return (
                    <ListItem
                      key={product.id}
                      button
                      onClick={() =>
                        window.open(`/admin/products/${product.id}`, '_blank')
                      }
                    >
                      <ListItemAvatar>
                        <Avatar
                          alt={product.name}
                          src={getImageUrl(product.mainImageId)}
                        />
                      </ListItemAvatar>
                      <ListItemText primary={product.name} />
                    </ListItem>
                  );
                })}
              </List>
            </div>
          }
          onCancel={toggleConfirmModalOpen}
          onClose={toggleConfirmModalOpen}
          icon={<HelpOutline fontSize="large" />}
          onConfirm={() => {
            onDeleteValue();
            toggleConfirmModalOpen();
          }}
        />
      )}
    </div>
  );
};

export default EditVariantValue;
